import React, { useEffect, useRef, useState } from 'react'
import { Container } from '../../../../../components/Container'
import {
    IconButton,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import StarOutlineIcon from '@material-ui/icons/StarOutline'
import StarIcon from '@material-ui/icons/Star'
import AddIcon from '@material-ui/icons/Add'
import { makeStyles } from '@material-ui/core/styles'

import { TypingIndicator } from './TypingIndicator'
import {
    AGENT_DRAWER_WIDTH,
    LORI_CHANNEL_HEADER_HEIGHT,
    LORI_CHANNEL_INPUT_HEIGHT,
} from '../../../../constants'
import { LoriText } from '../../Icons/LoriText'
import { ChatMessage } from './ChatMessage'
import { TrainingVideoController } from '../../../../../hooks/useTrainingVideo'
import { ProjectPlanController } from '../../../../../hooks/useProjectPlan'
import { useLoriState } from '../../../../../contexts/LoriContext'
import { useLocation } from 'react-router-dom'
import { ChatInput } from './ChatInput'
import { useLoriVoice } from '../../../../../hooks/useLoriVoice'

// Make sure Props is only defined once and properly typed for component usage.
interface Props {
    trainingVideoController: TrainingVideoController
    projectPlanController: ProjectPlanController
}

export const LoriContent: React.FC<Props> = ({
    trainingVideoController,
    projectPlanController,
}) => {
    const {
        connectionReady,
        thinking,
        userInput,
        handleSendMessage,
        setUserInput,
    } = useLoriState()
    const theme = useTheme()
    const {
        activeConversation,
        setActiveConversation,
        createConversation,
        activeAgent,
    } = useLoriState()
    const [progress, setProgress] = useState(0)
    const activeURL = useLocation().pathname
    const { recording, startRecording, stopRecording } = useLoriVoice(activeURL)

    // Time-based progress simulation
    useEffect(() => {
        if (!connectionReady && progress < 95) {
            const timer = setTimeout(() => {
                // Gradually slow down progress as it approaches 95%
                const increment = Math.max(1, (95 - progress) / 10)
                setProgress((prev) => Math.min(95, prev + increment))
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [progress, connectionReady])

    // Reset progress when connection is ready
    useEffect(() => {
        if (connectionReady) {
            setProgress(0)
        }
    }, [connectionReady])

    // Scroll to bottom of chat history
    const messageContainerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop =
                messageContainerRef.current.scrollHeight
        }
    }, [activeConversation?.chat_messages])

    return (
        <Container
            style={{
                flexDirection: 'column',
                width: AGENT_DRAWER_WIDTH,
                height: '100vh',
                position: 'relative',
                display: 'flex',
            }}
        >
            {/* Header - fixed height */}
            <Container
                style={{
                    height: LORI_CHANNEL_HEADER_HEIGHT,
                    minHeight: LORI_CHANNEL_HEADER_HEIGHT,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    alignItems: 'center',
                    padding: theme.spacing(1),
                    flexDirection: 'row',
                    zIndex: theme.zIndex.drawer + 5,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                {/* Header content container */}
                <Container
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        gap: theme.spacing(2),
                    }}
                >
                    {/* Logo container */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LoriText width={100} height={30} color="#000" />
                    </div>

                    {/* Turn Pro text */}
                    {activeAgent == 'schedule_project' && (
                        <Typography
                            variant="subtitle1"
                            style={{
                                marginLeft: theme.spacing(2),
                                color: '#fff',
                                fontWeight: 700,
                                backgroundColor: '#1976d2',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Turn Pro
                        </Typography>
                    )}
                </Container>
                {/* Pin Conversation */}
                <Tooltip title="Pin Conversation">
                    <IconButton
                        style={{
                            borderRadius: '50%',
                            border: `1px solid ${theme.palette.grey[300]}`,
                            padding: '6px',
                            marginLeft: theme.spacing(1),
                            backgroundColor: theme.palette.background.paper,
                            transition: 'background-color 0.3s ease',
                        }}
                        onClick={() => {
                            if (!activeConversation) return
                            const updatedConversation = {
                                ...activeConversation,
                                pinned: !activeConversation.pinned,
                            }
                            setActiveConversation(updatedConversation)
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.grey[400]
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.background.paper
                        }}
                    >
                        {activeConversation?.pinned ? (
                            <StarIcon htmlColor="#f2d70c" />
                        ) : (
                            <StarOutlineIcon />
                        )}
                    </IconButton>
                </Tooltip>

                {/* New Conversation */}
                <Tooltip title="New Conversation">
                    <IconButton
                        style={{
                            borderRadius: '50%',
                            border: `1px solid ${theme.palette.grey[300]}`,
                            padding: '6px',
                            marginLeft: theme.spacing(1),
                            backgroundColor: theme.palette.background.paper,
                            transition: 'background-color 0.3s ease',
                        }}
                        onClick={() => {
                            createConversation({ agent: 'triage' })
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.grey[400]
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.background.paper
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Container>

            {/* Chat Messages - flexible height */}
            <Container
                ref={messageContainerRef}
                style={{
                    flex: 1,
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: theme.spacing(0, 1, 1, 1),
                    flexDirection: 'column',
                    // backgroundColor: activeAgent == 'schedule_project' ? 'rgba(81, 81, 206, 0.25)' : 'transparent',
                }}
            >
                {/* Always render messages if they exist */}
                {activeConversation?.chat_messages.map((message, index) => {
                    return (
                        <ChatMessage
                            key={index}
                            message={message}
                            trainingVideoController={trainingVideoController}
                            projectPlanController={projectPlanController}
                        />
                    )
                })}

                {thinking && <TypingIndicator theme={theme} />}
            </Container>

            {/* Input Container - fixed height */}
            <Container
                style={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: theme.spacing(1),
                    alignItems: 'flex-start',
                    bottom: 0,
                    backgroundColor: theme.palette.background.paper,
                    minHeight: LORI_CHANNEL_INPUT_HEIGHT,
                }}
            >
                <ChatInput
                    userInput={userInput}
                    setUserInput={setUserInput}
                    handleSendMessage={handleSendMessage}
                    connectionReady={connectionReady}
                    progress={progress}
                    recording={recording}
                    startRecording={startRecording}
                    stopRecording={stopRecording}
                    theme={theme}
                    activeURL={activeURL}
                />
            </Container>
        </Container>
    )
}
